// Einstellungen › E-Mail-Eingang (docs/anfragen-zentrale/SPEZIFIKATION.md §3): Postfach des Betriebs einrichten (IMAP,
// Kennwort versiegelt), Verbindung testen, eingegangene Nachrichten ansehen und eine E-Mail-Datei (.eml) einlesen.
//
// Die Arbeit macht der Kern (`mail.inbound.*` aus '@kernel'): Diese Verträge legen Recht, Form und Audit fest. Das Recht
// `platform.mail.inbox` ist sicherheitskritisch (sensitive): Wer es hält, kann Postfächer anbinden und alle eingegangenen
// Nachrichten lesen – jede ändernde Aktion verlangt deshalb eine frische Anmeldung. Das Kennwort geht nur hinein, nie
// heraus. Dem KI-Assistenten sind alle Wege vorenthalten (assistant: 'never').
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'

export const MAIL_INBOX_PERMISSION = 'platform.mail.inbox'

const zSecurity = z.enum(['tls', 'starttls'])

export const zMailInboxSettings = z.object({
  configured: z.boolean(),
  host: z.string().nullable(),
  port: z.int(),
  security: zSecurity,
  username: z.string().nullable(),
  folder: z.string(),
  active: z.boolean(),
  markSeen: z.boolean(),
  /** ein Kennwort ist hinterlegt – es selbst kommt nie zurück */
  passwordSet: z.boolean(),
  updatedAt: z.date().nullable(),
  state: z
    .object({
      lastRunAt: z.date().nullable(),
      lastSuccessAt: z.date().nullable(),
      lastError: z.string().nullable(),
      lastErrorKind: z.enum(['temporary', 'auth', 'config']).nullable(),
      failures: z.int(),
      receivedTotal: z.int(),
    })
    .nullable(),
  counts: z.object({ received: z.int(), assigned: z.int(), ignored: z.int() }),
})

export const mailInboxGet = defineContract({
  name: 'platform.mailInbox.get',
  title: 'E-Mail-Eingang anzeigen',
  description: 'Postfach für eingehende E-Mails: Server, Benutzer, Ordner, aktiv – ohne Kennwort – und der Stand des Abrufs.',
  llm: { description: 'Einstellungen des E-Mail-Eingangs. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({}),
  output: zMailInboxSettings,
  permission: MAIL_INBOX_PERMISSION,
  risk: 'read',
  assistant: 'never',
})

const zSettingsInput = {
  host: z.string().trim().min(1, { error: 'Bitte geben Sie den Mailserver an.' }).max(260),
  port: z.int({ error: 'Der Port ist eine Zahl.' }),
  security: zSecurity,
  username: z.string().trim().min(1, { error: 'Bitte geben Sie den Benutzernamen an.' }).max(254),
  /** neues Kennwort; leer = das hinterlegte behalten */
  password: z.string().max(500).nullish(),
  folder: z.string().trim().min(1, { error: 'Bitte geben Sie den Ordner an.' }).max(200).default('INBOX'),
}

export const mailInboxUpdate = defineContract({
  name: 'platform.mailInbox.update',
  title: 'E-Mail-Eingang einrichten',
  description: 'Speichert das Postfach für eingehende E-Mails (das Kennwort versiegelt) und schaltet den Abruf ein oder aus.',
  llm: { description: 'Richtet den E-Mail-Eingang ein. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ ...zSettingsInput, active: z.boolean().default(false), markSeen: z.boolean().default(true) }),
  output: zMailInboxSettings,
  permission: MAIL_INBOX_PERMISSION,
  risk: 'write',
  assistant: 'never',
  // Kennwort und Ziel eines Postfachs: frische Anmeldung, keine Eingabe im Audit-Protokoll (das Kennwort stünde sonst darin)
  effects: { securitySensitive: true },
  auditInput: 'omit',
})

export const mailInboxTest = defineContract({
  name: 'platform.mailInbox.test',
  title: 'Verbindung zum Postfach testen',
  description: 'Meldet sich mit den eingegebenen (oder gespeicherten) Angaben am Postfach an, wählt den Ordner und zählt – holt nichts ab.',
  llm: { description: 'Testet die Verbindung zum Postfach. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject(zSettingsInput),
  output: z.object({
    ok: z.boolean(),
    message: z.string(),
    kind: z.enum(['temporary', 'auth', 'config']).nullable(),
    messages: z.int().nullable(),
    unseen: z.int().nullable(),
  }),
  permission: MAIL_INBOX_PERMISSION,
  risk: 'read',
  assistant: 'never',
  auditInput: 'omit',
})

export const zInboundMessageRow = z.object({
  id: zId,
  source: z.enum(['imap', 'upload']),
  receivedAt: z.date(),
  sentAt: z.date().nullable(),
  fromAddress: z.string().nullable(),
  fromText: z.string(),
  subject: z.string(),
  /** die ersten Zeichen des Textes */
  preview: z.string(),
  attachmentCount: z.int(),
  status: z.enum(['received', 'assigned', 'ignored']),
  statusLabel: z.string(),
  /** Bezug in einem Modul (Anliegen) – Ziel der Verknüpfung in der Oberfläche */
  assigned: z.object({ type: z.string(), id: zId }).nullable(),
  notes: z.array(z.string()),
})

export const mailInboxMessages = defineContract({
  name: 'platform.mailInbox.messages',
  title: 'Eingegangene E-Mails anzeigen',
  description: 'Die jüngsten eingegangenen E-Mails (Postfach und eingelesene Dateien) mit Absender, Betreff und Zuordnung.',
  llm: { description: 'Liste der eingegangenen E-Mails. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ limit: z.int().min(1).max(200).default(50) }),
  output: z.object({ rows: z.array(zInboundMessageRow) }),
  permission: MAIL_INBOX_PERMISSION,
  risk: 'read',
  assistant: 'never',
})

export const mailInboxImportEml = defineContract({
  name: 'platform.mailInbox.importEml',
  title: 'E-Mail-Datei (.eml) einlesen',
  description: 'Liest eine hochgeladene E-Mail-Datei wie eine eingegangene E-Mail ein – sie durchläuft denselben Weg wie aus dem Postfach.',
  llm: { description: 'Liest eine E-Mail-Datei ein. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ fileId: zId }),
  output: z.object({ id: zId, duplicate: z.boolean(), message: z.string() }),
  permission: MAIL_INBOX_PERMISSION,
  risk: 'write',
  assistant: 'never',
  effects: { securitySensitive: true },
})
